import { Fragment, FunctionalComponent, h } from "preact";
import { lazy, Suspense } from "preact/compat";
import Router, { route, Route, RouterOnChangeArgs } from "preact-router";

import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  setAliasMode,
  setPortalAlias,
  setPortalId,
  setWorkspaceAlias,
  setWorkspaceId,
} from "../../redux/slices/aliasSlice";
import {
  setMode,
  setPreview,
  setNarrowFactor,
  setWsd,
  setLogoSrc,
  setBackgroundImage,
  setBgPortraitImage,
} from "../../redux/slices/appearanceSlice";

import { setPreviewSasToken } from "../../redux/slices/authSlice";
import { useSasToken } from "../../hooks/useSasToken";

import { get_DASHBOARD_URI_BASE, getIsEdge } from "../../configs/env";
import {
  Utils,
  getAnonPortalSession,
  getEdgeUserLoginSession,
  isEdgeUserSessionExpired,
  saveEdgeUserLoginSession,
  setTheme,
  withCdnToken,
  resetPortalFlags,
} from "../../utils";
import { startEdgeSessionManager } from "../../api/edge-session";
import { routeIsPublicExperience } from "./publicExperience";

import axios from "axios";
import logger from "../../logger";

import { getIsPreview, getEffectiveMode } from "./previewMode";

import {
  setClientIp,
  setJWTUserIdToken,
  setUserId,
  setUserIdRan,
  setCustomUserId,
} from "../../redux/slices/globalsSlice";
import { useState, useEffect } from "preact/hooks";

// Lazy load all route components for code splitting
const Home = lazy(() => import("../../routes/home"));
const Search = lazy(() => import("../../routes/search"));
const Pdf = lazy(() => import("../../routes/pdf"));
const Video = lazy(() => import("../../routes/video"));
const Sheet = lazy(() => import("../../routes/sheet"));
const Notfound = lazy(() => import("../../routes/notfound"));
const CustomError = lazy(() => import("../../routes/customError"));
const Login = lazy(() => import("../../routes/login"));
const KorraCopilot = lazy(() => import("../../routes/korra-copilot"));
const LoginPage = lazy(() => import("../../routes/edgeLoginPage"));
const Portal = lazy(() => import("../../routes/analytics_app"));
const KeywordSearch = lazy(
  () => import("../../routes/keywordSearch/keywordSearch"),
);
const KeywordLayout = lazy(() => import("../../routes/home/keywordLayout"));

// Simple loading spinner
const LoadingSpinner = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
    }}
  >
    <div>Loading...</div>
  </div>
);

// Suspense fallback that renders nothing until `delay` ms have passed, so fast
// chunk loads (cached / already-preloaded) never flash the spinner. Only loads
// slower than the threshold ever show it.
const DelayedFallback = ({ delay = 200 }: { delay?: number }) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return show ? <LoadingSpinner /> : null;
};

// Route chunk loaders. Exposed so nav elements can preload on hover/focus.
export const routePreloaders: Record<string, () => Promise<unknown>> = {
  home: () => import("../../routes/home"),
  search: () => import("../../routes/search"),
  keywordSearch: () => import("../../routes/keywordSearch/keywordSearch"),
  keywordLayout: () => import("../../routes/home/keywordLayout"),
  pdf: () => import("../../routes/pdf"),
  video: () => import("../../routes/video"),
  sheet: () => import("../../routes/sheet"),
  korraCopilot: () => import("../../routes/korra-copilot"),
  analyticsApp: () => import("../../routes/analytics_app"),
};

// Preload route chunks only once the current page has fully loaded and the
// browser is idle, so preloading never competes with the current page's load.
const preloadRoutes = () => {
  const runWhenIdle = () => {
    const schedule =
      window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
    // One chunk per idle slice so the queue can't monopolize the main thread.
    Object.values(routePreloaders).forEach((load) =>
      schedule(() => {
        load().catch(() => {}); // ignore failed preloads; real nav will surface the error
      }),
    );
  };

  if (document.readyState === "complete") {
    runWhenIdle();
  } else {
    window.addEventListener("load", runWhenIdle, { once: true });
  }
};

export interface RouterProps {
  workspaceId: string;
  kappId: string;
  portalId: string;
  workspaceAlias: string;
  portalAlias: string;

  // editor
  preview?: string;

  // search
  q: string;
  back: boolean;
  // pdf / video
  guid: string;

  // pdf
  docId: string; // docGuid
  page: string;

  // video
  docGuid: string;

  // widget
  mode: "widget";

  // narrowfactor
  narrowfactor: string;

  sasTokenPreview?: string;
  chatbotFlag?: boolean;
  userID?: string;

  // korra-copilot
  model?: string;
  problem?: string;
}

// Builds the /edge/login URL to bounce an unauthenticated or mismatched-session
// user to, preserving the current location as a `redirect` query param so the
// login page can send them back afterwards (see pickTeamForRedirect). Returns
// the bare "/edge/login" for an empty/root target, or when already on
// /edge/login, so redirecting from there can never grow the URL into a loop.
const buildEdgeLoginRedirectUrl = () => {
  const target =
    window.location.pathname + window.location.search + window.location.hash;
  if (!target || target === "/" || target.includes("/edge/login")) {
    return "/edge/login";
  }
  return `/edge/login?redirect=${encodeURIComponent(target)}`;
};

const AppRouter: FunctionalComponent = () => {
  const appearance = useAppSelector((state) => state.appearance);
  const globals = useAppSelector((state) => state.globals);
  const alias = useAppSelector((state) => state.alias);
  const auth = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const [isEdge, setIsEdge] = useState<boolean | undefined>(getIsEdge());

  // Preload other route chunks after the current page has loaded (idle-time),
  // so first navigation doesn't flash the Suspense fallback.
  useEffect(() => {
    preloadRoutes();
  }, []);

  // The theme is seeded before the CDN token is minted, so branding URLs are
  // built without one. On edge that is refused for a portal that is not public,
  // leaving the logo and background blank. Stamp the token on when it lands.
  // sticky: the branding keeps its first token, so the images don't re-download
  // on every refresh. withCdnToken swaps an old token for a new one (a new
  // portal or user); an unchanged URL is a no-op dispatch and ends the loop.
  const sasToken = useSasToken(undefined, undefined, { sticky: true });
  useEffect(() => {
    if (!sasToken) return;
    if (appearance.logoSrc) {
      dispatch(setLogoSrc(withCdnToken(appearance.logoSrc, sasToken)));
    }
    if (appearance.backgroundImage) {
      dispatch(
        setBackgroundImage(withCdnToken(appearance.backgroundImage, sasToken)),
      );
    }
    if (appearance.bgPortraitImage) {
      dispatch(
        setBgPortraitImage(withCdnToken(appearance.bgPortraitImage, sasToken)),
      );
    }
  }, [
    sasToken,
    appearance.logoSrc,
    appearance.backgroundImage,
    appearance.bgPortraitImage,
  ]);

  /**
   * error 400
   * @param routerProps
   */
  const route400 = (routerProps: RouterProps) => {
    logger.warn({
      message: "Something wrong with routerProps",
      router_props: routerProps,
      portal_alias: alias.portalAlias,
      workspace_id: alias.workspaceId,
    });
    route(`/error/${400}/errorMessage/${"Bad URL."}`);
  };

  const route422 = (errorMessage: string) => {
    route(`/error/${422}/errorMessage/${errorMessage}`);
  };

  /**
   * on route change
   * @param args
   * @returns
   */
  /**
   * Establish the identity of a visitor to a public portal.
   *
   * They never log in, but they still need an identity: their saved items have
   * to be told apart from the next visitor's, and every document served to them
   * is watermarked. It used to be a uuid the browser minted for itself, which
   * meant the visitor chose it -- and so could choose someone else's. Auth mints
   * it now, and only for a portal it confirms is public.
   *
   * Both the id and the token it came with go into globals: userdata sends the
   * id and presents the token, and they have to agree or the request is refused.
   */
  const establishPublicSession = async (
    workspaceId?: string,
    portalId?: string,
  ) => {
    if (!workspaceId || !portalId) return;
    const anon = await getAnonPortalSession(workspaceId, portalId);
    if (!anon) return;
    dispatch(setUserId(anon.userId));
    dispatch(setJWTUserIdToken(anon.token));
    dispatch(setUserIdRan(true));
  };

  const onChange = async (args: RouterOnChangeArgs) => {
    try {
      const matches = (args.current.props as any)?.matches;
      // Going to login leaves the preview session; the preview token must not
      // outlive it and be served to the user who signs in.
      if (args.path && args.path.includes("/login") && auth.previewSasToken) {
        dispatch(setPreviewSasToken(""));
      }
      if (isEdge) {
        // OIDC callback in flight: the IdP redirected back to /edge/login with
        // ?code&state. Let the login page complete the code exchange — do NOT
        // let any session/workspace redirect below strip the query first (a
        // stale edge session would otherwise route("/edge/login", true) and
        // drop the code, sending us back into the login loop).
        const cbParams = new URLSearchParams(window.location.search);
        if (
          args.path?.includes("/edge/login") &&
          cbParams.has("code") &&
          cbParams.has("state")
        ) {
          return;
        }
        // Mosaic-to-Edge login: the auth service redirects here with a one-time
        // token in the URL fragment (#token=<jwt>&workspaceId=...). The fragment
        // is never sent to the server, so the JWT can't leak via proxy logs or
        // the Referer header. Consume it into an edge session so the user lands
        // logged in, then strip the fragment from the URL.
        const rawHash = window.location.hash.startsWith("#")
          ? window.location.hash.slice(1)
          : window.location.hash;
        const hashAuthParams = new URLSearchParams(rawHash);
        const tokenFromUrl = hashAuthParams.get("token");
        if (tokenFromUrl) {
          const wsId = hashAuthParams.get("workspaceId") ?? "";
          // One-shot Mosaic login correlation id: stash it so the first
          // session_manager/start (fired from appLogic) forwards it, letting the
          // resulting startSession event be joined back to this login. Use
          // sessionStorage (not localStorage) so the id is scoped to the tab that
          // actually received the redirect: it can't be read-and-cleared by an
          // unrelated tab's pending startSession, and it can't linger in another
          // tab to mis-attribute a future, unrelated login.
          const mosaicLoginId = hashAuthParams.get("loginId") ?? "";
          if (mosaicLoginId) {
            try {
              sessionStorage.setItem("Web-app-mosaicLoginId", mosaicLoginId);
            } catch (e) {
              // ignore storage failures; attribution just won't be available
            }
          }
          const mosaicUser = {
            workspaceId: wsId,
            // Identity comes from the fragment too; edge code paths key off
            // user.email (e.g. CDN SAS-token requests), so it must be set.
            email: hashAuthParams.get("email") ?? "",
            password: "",
            id: "",
            firstName: hashAuthParams.get("firstName") ?? "",
            lastName: hashAuthParams.get("lastName") ?? "",
            name: hashAuthParams.get("name") ?? "",
            isReseted: false,
            userIdToken: tokenFromUrl,
          };
          // The auth service redirects here (to "/") carrying only a workspaceId,
          // but every web-app route is /:workspaceAlias/:portalAlias. Resolve the
          // workspace's portal so we can (a) key the session by its portalAlias so
          // the guard below passes and (b) land on the real portal route instead
          // of bouncing to /edge/login.
          let landingPortalAlias = matches?.portalAlias ?? "";
          let landingWorkspaceAlias = "";
          try {
            const aliasRes = await axios.get(
              `${get_DASHBOARD_URI_BASE()}/alias-list-by-name/_`,
            );
            // The endpoint returns one entry per portal, so a workspace with
            // multiple portals yields multiple matches, and the Mosaic token
            // doesn't name a portal. Pick deterministically (cursor order isn't
            // stable):
            //   1. a portal whose alias matches the workspace alias, else
            //   2. prefer mode === "portal", then the lowest portalAlias
            // so the same user always lands in the same portal.
            const candidates = (aliasRes?.data?.data ?? []).filter(
              (p: any) =>
                p?.workspaceId === wsId && p?.portalAlias && p?.workspaceAlias,
            );
            candidates.sort((a: any, b: any) => {
              const aMatchesWs = a.portalAlias === a.workspaceAlias ? 0 : 1;
              const bMatchesWs = b.portalAlias === b.workspaceAlias ? 0 : 1;
              return (
                aMatchesWs - bMatchesWs ||
                (a.mode === "portal" ? 0 : 1) - (b.mode === "portal" ? 0 : 1) ||
                String(a.portalAlias).localeCompare(String(b.portalAlias))
              );
            });
            const pair = candidates[0];
            if (pair) {
              landingPortalAlias = pair.portalAlias;
              landingWorkspaceAlias = pair.workspaceAlias;
            }
          } catch (err) {
            logger.error({
              message: "Mosaic login: failed to resolve workspace portal",
              error: (err as any)?.message,
              stacktrace: (err as any)?.stack,
              workspace_id: wsId,
            });
          }
          // Key the session by the portal alias we're about to land on so the
          // workspaceId guard below passes.
          saveEdgeUserLoginSession(
            mosaicUser,
            landingPortalAlias,
            landingWorkspaceAlias,
          );
          // Drop the fragment so the token isn't left in the address bar/history.
          const cleanUrl = window.location.pathname + window.location.search;
          window.history.replaceState({}, "", cleanUrl);
          // Land the user straight in their portal (logged in). Without this the
          // bare "/" landing matches no route and would bounce to /edge/login.
          if (landingWorkspaceAlias && landingPortalAlias) {
            route(`/${landingWorkspaceAlias}/${landingPortalAlias}`, true);
            return;
          }
          // Landing lookup failed (alias endpoint errored or matched no portal),
          // so this Mosaic login won't cleanly establish a session and consume the
          // correlation id. Drop it now instead of leaving it in storage, where a
          // later unrelated login's startSession would read it and mis-attribute
          // its activity to this failed login.
          try {
            sessionStorage.removeItem("Web-app-mosaicLoginId");
          } catch (e) {
            // ignore storage failures
          }
        }
        const user = await getEdgeUserLoginSession();
        const onLoginPage = args.path?.includes("/edge/login");
        if (
          user &&
          !isEdgeUserSessionExpired(user?.timestamp, user?.user?.userIdToken)
        ) {
          // Existing session hydrated on load — arm the session manager (idle /
          // absolute / refresh enforcement). Idempotent, no-op off edge (#1100).
          startEdgeSessionManager();
          // Portal alias alone isn't unique across workspaces (two workspaces
          // can each have a portal aliased e.g. "docs"), so also compare the
          // workspace alias when the stored session has one.
          const workspaceMismatch =
            !!user.workspaceAlias &&
            user.workspaceAlias !== matches?.workspaceAlias;
          if (
            !onLoginPage &&
            (user.workspaceId !== matches?.portalAlias || workspaceMismatch)
          ) {
            // Preserve the deep link so /edge/login can select the right
            // team via pickTeamForRedirect instead of defaulting to the
            // first team when it's already logged into a different one.
            // Never log `user`: it carries userIdToken, a live session JWT
            // that would be persisted in Logstash. Email + aliases identify the
            // session well enough to debug a mismatch.
            logger.log({
              message: "App router redirected to login: workspace mismatch",
              email: user?.user?.email,
              session_workspace_alias: user?.workspaceAlias,
              session_portal_alias: user?.workspaceId,
              route_workspace_alias: matches?.workspaceAlias,
              portal_alias: matches?.portalAlias,
            });
            route(buildEdgeLoginRedirectUrl(), true);
            return;
          }
          // Edge login persists the workspace JWT only in localStorage; nothing
          // else copies it into redux, so fetch-based callers that read
          // globals.jwtUserIdToken (Dive Deeper, korra-copilot) sent no
          // Authorization header and webauth 401'd them on private portals.
          if (
            user.user?.userIdToken &&
            user.user.userIdToken !== globals.jwtUserIdToken
          ) {
            dispatch(setJWTUserIdToken(user.user.userIdToken));
            if (user.user.id) dispatch(setUserId(user.user.id));
          }
          // Same reason as above: no `user`, it holds the session JWT.
          logger.log({
            message: "App router changed",
            email: user?.user?.email,
            portal_alias: alias.portalAlias,
            workspace_id: user?.workspaceId,
          });
        } else if (!onLoginPage) {
          const params = new URLSearchParams(window.location.search);
          if (
            args.path?.includes("/edge/login") &&
            params.has("code") &&
            params.has("state")
          ) {
            return;
          }
          // A public experience has no sign-in to send anyone to. Fall through
          // instead of bouncing: once its appearance loads, establishPublicSession
          // (below) issues the anonymous session. Nobody signs in to a public
          // experience, so the login page has nothing to offer here.
          const publicRoute = await routeIsPublicExperience(
            matches?.workspaceAlias,
            matches?.portalAlias,
          );
          if (!publicRoute) {
            // Not signed in: send to login, preserving the target URL so we can
            // return the user there after a successful login. Return so the
            // route being abandoned doesn't still run its alias lookups and
            // dispatches below.
            route(buildEdgeLoginRedirectUrl(), true);
            return;
          }
        }
      }
      if (args.path?.includes("/login")) return;
      if (matches) {
        const routerProps = {
          workspaceId: matches?.workspaceId,
          kappId: matches?.kappId,
          portalId: matches?.portalId,
          workspaceAlias: (args.current.props as any)?.matches?.workspaceAlias,
          portalAlias: matches?.portalAlias,
          preview: matches?.preview,
          q: matches?.q,
          back: matches?.back,
          guid: matches?.guid,
          docId: matches?.docId,
          page: matches?.page,
          docGuid: matches?.docGuid,
          mode: matches?.mode,
          narrowfactor: matches?.narrowfactor,
          sasTokenPreview: matches?.sasTokenPreview,
          chatbotFlag: args.path?.includes("/chatbot"),
          userID: matches?.userID,
          model: matches?.model,
          problem: matches?.problem,
        } as RouterProps;
        const isPreview = getIsPreview(
          routerProps.preview,
          window.location.search,
        );
        if (isPreview) {
          dispatch(setPreview(true));
        }
        // The preview token is scoped to the previewed portal. Result links and
        // next-section routes inside the preview do not repeat it, so keep it
        // while the route stays in that portal; drop it once the route leaves,
        // and useSasToken goes back to minting.
        if (isPreview && routerProps.sasTokenPreview) {
          dispatch(
            setPreviewSasToken(decodeURIComponent(routerProps.sasTokenPreview)),
          );
        } else if (auth.previewSasToken) {
          const samePortal =
            (routerProps.portalAlias &&
              routerProps.portalAlias === alias.portalAlias) ||
            (routerProps.portalId && routerProps.portalId === alias.portalId) ||
            (!routerProps.portalAlias &&
              !routerProps.portalId &&
              routerProps.workspaceId === alias.workspaceId);
          if (!samePortal) dispatch(setPreviewSasToken(""));
        }

        if (appearance.loaded && !appearance.isPrivate && !globals.userIdRan) {
          //to make user we dont run the useEffect more than once just when appearance.loaded === true
          await establishPublicSession(alias.workspaceId, alias.portalId);
        }

        if (!appearance.narrowfactor && routerProps.narrowfactor == "1") {
          dispatch(setNarrowFactor(1));
        }

        if (routerProps.mode && routerProps.mode !== appearance.mode)
          dispatch(setMode(routerProps.mode));

        const urlParams = new URLSearchParams(window.location.search);
        const wsdFromUrl = urlParams.get("wsd") === "true";
        if (wsdFromUrl !== appearance.wsd) {
          dispatch(setWsd(wsdFromUrl));
        }

        if (routerProps.userID) {
          localStorage.setItem("korraCustomUserId", routerProps.userID);
          dispatch(setCustomUserId(routerProps.userID));
        }

        if (!globals.clientIp && !isEdge) {
          try {
            const _clientIp = await axios.get(
              "https://api.ipify.org?format=json",
            );
            if (_clientIp?.data?.ip) {
              dispatch(setClientIp(_clientIp.data.ip));
            }
          } catch (error) {
            logger.error({
              message: "Failed to get client IP",
              error: error,
              stacktrace: (error as any)?.stack,
              portal_alias: alias.portalAlias,
              workspace_id: alias.workspaceId,
            });
          }
        }

        if (
          routerProps.workspaceAlias &&
          routerProps.portalAlias &&
          !alias.workspaceId &&
          !alias.portalId
        ) {
          const debugg = get_DASHBOARD_URI_BASE();
          const url = `${get_DASHBOARD_URI_BASE()}/alias/${
            routerProps.workspaceAlias
          }/${routerProps.portalAlias}`;
          logger.log({
            message: "Alias lookup URLs",
            alias_url: url,
            workspace_alias: routerProps.workspaceAlias,
            portal_alias: routerProps.portalAlias,
          });
          const _aliasResponce = await axios(
            `${get_DASHBOARD_URI_BASE()}/alias/${routerProps.workspaceAlias}/${
              routerProps.portalAlias
            }`,
          );
          const aliasResponse = _aliasResponce?.data?.data;

          if (aliasResponse?.workspaceId && aliasResponse?.portalId) {
            // Get the portal details with themes
            const portalResponse = await axios(
              `${get_DASHBOARD_URI_BASE()}/portals/${
                aliasResponse.portalId
              }/workspaces/${aliasResponse.workspaceId}`,
            );
            const portalData = portalResponse?.data?.data;

            dispatch(setWorkspaceId(aliasResponse.workspaceId));
            dispatch(setPortalId(aliasResponse.portalId));
            dispatch(setWorkspaceAlias(routerProps.workspaceAlias));
            dispatch(setPortalAlias(routerProps.portalAlias));
            const effectiveMode =
              getEffectiveMode({
                isPreview,
                mode: portalData.mode,
                previewMode: portalData.previewMode,
              }) ?? "";
            dispatch(setAliasMode(effectiveMode));

            // Get the correct theme based on mode
            let selectedTheme;
            if (effectiveMode === "chatbot") {
              selectedTheme = portalData.chatThemes.find(
                (t: any) => t?.name === "publish",
              );
            } else {
              selectedTheme = portalData.themes.find(
                (t: any) => t?.name === "publish",
              );
            }

            if (selectedTheme) {
              setTheme(selectedTheme, dispatch, aliasResponse.workspaceId);
            } else {
              // No publish theme: nothing will overwrite the previous
              // portal's direction / year-range flags, so clear them.
              resetPortalFlags(dispatch);
            }

            const isPrivate = selectedTheme?.isPrivate;

            if (isPrivate) {
              if (isPreview) {
                dispatch(setPreview(true));
                return;
              }

              if (!auth.korraUser && !auth.auth0User && !isEdge) {
                Utils.routeToLogin({
                  workspaceAlias: routerProps.workspaceAlias,
                  portalAlias: routerProps.portalAlias,
                });
              }
            }
            if (!isPrivate && !globals.userIdRan) {
              await establishPublicSession(
                aliasResponse.workspaceId,
                aliasResponse.portalId,
              );
            }
          } else {
            route422("It is alias but couldn't get alias response");
          }
        } else if (
          routerProps.workspaceId &&
          (routerProps.portalId || routerProps.kappId)
        ) {
          dispatch(setWorkspaceId(routerProps.workspaceId));
          if (routerProps.portalId) {
            dispatch(setPortalId(routerProps.portalId));

            const theme = await Utils.getTheme(
              routerProps.workspaceId,
              routerProps.portalId,
              !routerProps.mode,
            );

            if (theme) {
              setTheme(theme, dispatch, routerProps.workspaceId);
            } else if (theme === undefined) {
              // The portal really has no publish theme, so nothing will
              // overwrite the previous portal's direction / year-range flags:
              // clear them. A null is a failed lookup, and this handler runs
              // on every navigation -- resetting there would let one timed-out
              // request un-mirror a live RTL portal, so leave the flags be.
              resetPortalFlags(dispatch);
            }
            if (theme?.isPrivate) {
              if (isPreview) {
                dispatch(setPreview(true));
                return;
              }
              if (!auth.korraUser && !auth.auth0User && !isEdge) {
                Utils.routeToLogin(null);
              }
            }
            if (!theme?.isPrivate && !globals.userIdRan) {
              //to make user we dont run the useEffect more than once just when appearance.loaded === true
              await establishPublicSession(
                routerProps.workspaceId,
                routerProps.portalId,
              );
            }
          } else if (routerProps.kappId) {
            // dispatch(setKappId(routerProps.kappId));
            // no need theme, no need alias, all is ok, it is kapp -- but a
            // theme is also what clears the previous portal's direction and
            // year-range flags, so clear them here by hand.
            resetPortalFlags(dispatch);
          } else {
            route400(routerProps);
          }
        } else {
          if (
            appearance.isPrivate &&
            (!alias.workspaceAlias || !alias.workspaceId)
          ) {
            if (isPreview) {
              dispatch(setPreview(true));
              return;
            }
            route400(routerProps);
          }
        }
      } else {
        route422("No matches at URL params");
      }
    } catch (error: any) {
      logger.error({
        message: "Error in router onChange handler",
        error: error,
        stacktrace: error?.stack,
        portal_alias: alias.portalAlias,
        workspace_id: alias.workspaceId,
        path: args.path,
        error_detail: error?.message || "",
      });
      route422("Something wrong: " + error?.message || "");
    }
  };

  return (
    <Suspense fallback={<DelayedFallback />}>
      <Router onChange={onChange}>
        <Route path="/workspace/:workspaceId/kapp/:kappId" component={Home} />
        <Route
          path="/workspace/:workspaceId/kapp/:kappId/home"
          component={Home}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/korra-copilot"
          component={KorraCopilot}
        />
        {/* Legacy chatbot links are served by Korra Copilot. */}
        <Route
          path="/workspace/:workspaceId/portal/:portalId/chatbot"
          component={KorraCopilot}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/analytics_app"
          component={Portal}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/analytics_app/:conversationId"
          component={Portal}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId"
          component={Home}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/home"
          component={Home}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/:preview/home"
          component={Home}
        />
        <Route
          path="/workspace/:workspaceId/kapp/:kappId/search"
          component={Search}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/search"
          component={Search}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/generalAIHome"
          component={Home}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/generalAISearch"
          component={Search}
        />
        <Route
          path="/workspace/:workspaceId/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/workspace/:workspaceId/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />

        <Route
          path="/workspace/:workspaceId/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />
        <Route
          path="/workspace/:workspaceId/portal/:portalId/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />

        <Route path="/company/:workspaceId/kapp/:kappId" component={Home} />
        <Route
          path="/company/:workspaceId/kapp/:kappId/home"
          component={Home}
        />
        <Route path="/company/:workspaceId/portal/:portalId" component={Home} />
        <Route
          path="/company/:workspaceId/portal/:portalId/home"
          component={Home}
        />
        <Route
          path="/company/:workspaceId/portal/:portalId/:preview/home"
          component={Home}
        />
        <Route
          path="/company/:workspaceId/kapp/:kappId/search"
          component={Search}
        />
        <Route
          path="/company/:workspaceId/portal/:portalId/search"
          component={Search}
        />
        <Route
          path="/company/:workspaceId/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/company/:workspaceId/portal/:portalId/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/company/:workspaceId/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />
        <Route
          path="/company/:workspaceId/portal/:portalId/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />
        <Route
          path="/company/:workspaceId/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />
        <Route
          path="/company/:workspaceId/portal/:portalId/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />
        <Route path="/analytics_app" component={Portal} />
        <Route path="/analytics_app/:conversationId" component={Portal} />
        <Route
          path="/:workspaceAlias/:portalAlias/analytics_app"
          component={Portal}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/analytics_app/:conversationId"
          component={Portal}
        />

        {/*  */}

        <Route path="/:workspaceAlias/:portalAlias" component={Home} />
        <Route path="/:workspaceAlias/:portalAlias/home" component={Home} />
        <Route
          path="/:workspaceAlias/:portalAlias/generalAIHome"
          component={Home}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/keywordHome"
          component={KeywordLayout}
        />
        <Route path="/:workspaceAlias/:portalAlias/search" component={Search} />
        <Route
          path="/:workspaceAlias/:portalAlias/generalAISearch"
          component={Search}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/keywordSearch"
          component={KeywordSearch}
        />
        <Route path="/:workspaceAlias/:portalAlias/login" component={Login} />
        <Route
          path="/:workspaceAlias/:portalAlias/korra-copilot"
          component={KorraCopilot}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/chatbot"
          component={KorraCopilot}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/korra-copilot?mode=widget"
          component={KorraCopilot}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/chatbot?mode=widget"
          component={KorraCopilot}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/analytics_app?mode=widget"
          component={Portal}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/kapp/:kappId/video/docGuid/:docGuid/guid/:guid/chatbotFlag/:chatbotFlag"
          component={Video}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid/chatbotFlag/:chatbotFlag"
          component={Pdf}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />
        <Route
          path="/:workspaceAlias/:portalAlias/kapp/:kappId/sheet/docGuid/:docId/guid/:guid/chatbotFlag/:chatbotFlag"
          component={Sheet}
        />

        {/*  */}

        <Route
          path="/error/:statusCode/errorMessage/:errorMessage"
          component={CustomError}
        />

        {isEdge ? <LoginPage default /> : <Notfound default />}

        {/*  */}
        <Route
          path="/:workspaceAlias/:portalAlias/login/redirect/:redirect"
          component={Login}
        />
        <Route path="/login/redirect/:redirect" component={Login} />
        <Route path="/login" component={Login} />

        {/* edge routes */}
        <Route path="/edge/login" component={LoginPage} />
        <Route
          path="/app/workspace/:workspaceId/kapp/:kappId"
          component={Home}
        />
        <Route
          path="/app/workspace/:workspaceId/kapp/:kappId/home"
          component={Home}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId/korra-copilot"
          component={KorraCopilot}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId/chatbot"
          component={KorraCopilot}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId"
          component={Home}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId/home"
          component={Home}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId/:preview/home"
          component={Home}
        />
        <Route
          path="/app/workspace/:workspaceId/kapp/:kappId/search"
          component={Search}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId/search"
          component={Search}
        />
        <Route
          path="/app/workspace/:workspaceId/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/app/workspace/:workspaceId/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />

        <Route
          path="/app/workspace/:workspaceId/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />
        <Route
          path="/app/workspace/:workspaceId/portal/:portalId/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />

        <Route path="/app/company/:workspaceId/kapp/:kappId" component={Home} />
        <Route
          path="/app/company/:workspaceId/kapp/:kappId/home"
          component={Home}
        />
        <Route
          path="/app/company/:workspaceId/portal/:portalId"
          component={Home}
        />
        <Route
          path="/app/company/:workspaceId/portal/:portalId/home"
          component={Home}
        />
        <Route
          path="/app/company/:workspaceId/portal/:portalId/:preview/home"
          component={Home}
        />
        <Route
          path="/app/company/:workspaceId/kapp/:kappId/search"
          component={Search}
        />
        <Route
          path="/app/company/:workspaceId/portal/:portalId/search"
          component={Search}
        />
        <Route
          path="/app/company/:workspaceId/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/app/company/:workspaceId/portal/:portalId/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/app/company/:workspaceId/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />
        <Route
          path="/app/company/:workspaceId/portal/:portalId/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />
        <Route
          path="/app/company/:workspaceId/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />
        <Route
          path="/app/company/:workspaceId/portal/:portalId/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />

        {/*  */}

        <Route path="/app/:workspaceAlias/:portalAlias" component={Home} />
        <Route path="/app/:workspaceAlias/:portalAlias/home" component={Home} />
        <Route
          path="/app/:workspaceAlias/:portalAlias/search"
          component={Search}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/keywordSearch"
          component={KeywordSearch}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/login"
          component={Login}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/korra-copilot"
          component={KorraCopilot}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/chatbot"
          component={KorraCopilot}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/korra-copilot?mode=widget"
          component={KorraCopilot}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/chatbot?mode=widget"
          component={KorraCopilot}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/kapp/:kappId/video/docGuid/:docGuid/guid/:guid"
          component={Video}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/kapp/:kappId/video/docGuid/:docGuid/guid/:guid/chatbotFlag/:chatbotFlag"
          component={Video}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid/chatbotFlag/:chatbotFlag"
          component={Pdf}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/kapp/:kappId/pdf/docGuid/:docId/page/:page/guid/:guid"
          component={Pdf}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/kapp/:kappId/sheet/docGuid/:docId/guid/:guid"
          component={Sheet}
        />
        <Route
          path="/app/:workspaceAlias/:portalAlias/kapp/:kappId/sheet/docGuid/:docId/guid/:guid/chatbotFlag/:chatbotFlag"
          component={Sheet}
        />

        {/*  */}

        <Route
          path="/app/error/:statusCode/errorMessage/:errorMessage"
          component={CustomError}
        />

        {/*  */}
        <Route
          path="/app/:workspaceAlias/:portalAlias/login/redirect/:redirect"
          component={Login}
        />
        <Route path="/app/login/redirect/:redirect" component={Login} />
        <Route path="/app/login" component={Login} />

        {/* private */}
      </Router>
    </Suspense>
  );
};

export default AppRouter;
